import { ApiError, apiStream, clientOrigin } from './client';
import { createSseParser } from './ai';

export interface LiveEvent {
  id: string;
  ts: string;
  actor_pid?: string | null;
  origin?: string | null;
  account_pid?: string | null;
  site_slug?: string | null;
  domain: string;
  action: string;
  row_id?: string | null;
  content_type?: string | null;
  notice?: string | null;
  toast?: { level: 'success' | 'error' | 'info'; text: string } | null;
}

function isEvent(value: unknown): value is LiveEvent {
  if (!value || typeof value !== 'object') return false;
  const e = value as Record<string, unknown>;
  return ['id', 'ts', 'domain', 'action'].every((key) => typeof e[key] === 'string')
    && ['actor_pid', 'origin', 'account_pid', 'site_slug', 'row_id', 'content_type', 'notice']
      .every((key) => e[key] == null || typeof e[key] === 'string')
    && (e.toast == null || (typeof e.toast === 'object'
      && ['success', 'error', 'info'].includes((e.toast as Record<string, unknown>).level as string)
      && typeof (e.toast as Record<string, unknown>).text === 'string'));
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const finish = () => { clearTimeout(timer); signal.removeEventListener('abort', finish); resolve(); };
    const timer = setTimeout(finish, ms);
    signal.addEventListener('abort', finish, { once: true });
    if (signal.aborted) finish();
  });
}

/** A single authenticated connection. Abort always releases its reader, including while idle. */
export async function* streamEvents(signal: AbortSignal, streamFetch?: typeof fetch): AsyncGenerator<LiveEvent> {
  const response = await apiStream('/v1/events', undefined, signal, streamFetch, 'GET');
  if (!response.body) throw new Error('Live events response has no stream');
  const reader = response.body.getReader();
  const cancel = () => { void reader.cancel().catch(() => {}); };
  signal.addEventListener('abort', cancel, { once: true });
  try {
    const decoder = new TextDecoder();
    const parse = createSseParser(256 * 1024);
    while (!signal.aborted) {
      const chunk = await reader.read();
      if (chunk.done) break;
      for (const frame of parse(decoder.decode(chunk.value, { stream: true }))) {
        if (frame.event === 'change' && isEvent(frame.data)) yield frame.data;
      }
    }
  } finally {
    signal.removeEventListener('abort', cancel);
    await reader.cancel().catch(() => {});
  }
}

/** Caller owns auth/foreground/billing lifecycle. A 402 stays quiet until that lifecycle restarts. */
export async function streamLiveEvents(options: {
  signal: AbortSignal;
  onEvent: (event: LiveEvent) => void;
  onResync: () => void;
  onConnected: (connected: boolean) => void;
  streamFetch?: typeof fetch;
}): Promise<'stopped' | 'unauthorized' | 'upgrade_required'> {
  const { signal, onEvent, onResync, onConnected, streamFetch } = options;
  let backoff = 1000;
  const seen = new Set<string>();
  while (!signal.aborted) {
    try {
      let synchronized = false;
      for await (const event of streamEvents(signal, streamFetch)) {
        if (signal.aborted) break;
        backoff = 1000;
        if (!synchronized) onConnected(true);
        if (event.action === 'resync') { onResync(); synchronized = true; continue; }
        // A server restart/reconnect has no replay: refresh active queries before new changes.
        if (!synchronized) { onResync(); synchronized = true; }
        if (event.origin === clientOrigin || seen.has(event.id)) continue;
        seen.add(event.id);
        if (seen.size > 256) seen.delete(seen.values().next().value!);
        onEvent(event);
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 402) return 'upgrade_required';
      if (error instanceof ApiError && error.status === 401) return 'unauthorized';
      if (signal.aborted) break;
    } finally {
      onConnected(false);
    }
    await pause(backoff, signal);
    backoff = Math.min(backoff * 2, 30_000);
  }
  return 'stopped';
}
